import React from 'react';
import PropTypes from 'prop-types';
import {locationShape, routerShape, withRouter} from 'react-router';
import {action, reaction} from 'mobx';
import {inject, observer} from 'mobx-react';
import autobind from 'autobind-decorator';
import * as Sentry from '@sentry/react';
import loadable from '@loadable/component';

import isValidException from 'utils/isValidException';
import {Form, FormHandler, InputField, PasswordField, Divider} from 'spa/Components/Form';
import Button from '@urbanmassage/component-library/lib/Button/Button';
import {withI18n} from 'spa/Components/Context';
import LoginWithFacebook from './LoginWithFacebook';

const VerifyAccountRequest = loadable(() => import('./VerifyAccountRequest'));

@withRouter
@inject(({store: {notifications, session, tracker}}) => (
  {notifications, session, tracker}
))
@observer
@withI18n
class LoginForm extends React.Component {
  static propTypes = {
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
      dismiss: PropTypes.func.isRequired,
    }).isRequired,
    session: PropTypes.shape({
      login: PropTypes.func.isRequired,
      isLoading: PropTypes.bool.isRequired,
      isVerificationChallengeRequired: PropTypes.bool.isRequired,
      error: PropTypes.any,
      loginResponse: PropTypes.any,
      isLoggedIn: PropTypes.bool.isRequired,
      resetLogin: PropTypes.func.isRequired,
      resetError: PropTypes.func.isRequired,
    }).isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
    location: locationShape.isRequired,
    router: routerShape.isRequired,
    showVoucherField: PropTypes.bool,
    onLoginSuccess: PropTypes.func,
    signupCampaign_id: PropTypes.string,
  };

  static defaultProps = {
    showVoucherField: false,
    signupCampaign_id: undefined,
    trackingCategory: 'Login',
  }

  UNSAFE_componentWillMount() {
    this.props.session.resetError();

    const {location} = this.props;
    const {query} = location;

    if (query.code) {
      this.voucherCodeValue = query.code;
    }
  }

  componentDidMount() {
    const {tracker, trackingCategory} = this.props;
    this.listeners = [
      reaction(
        () => this.props.session.error,
        (error) => {
          if (error) {
            if (error.code === 400 && error.status === 'INVALID') {
              this.props.notifications.add({
                id: 'login-message',
                type: 'error',
                title: this.context.i18n._x('You must provide an email address and a password', 'Login Form'),
              });
            } else {
              if (error.json && error.json.code === 'ACCOUNT_VERIFY_CHALLENGE') {
                tracker.track('Account verification required', {
                  category: trackingCategory,
                });

                return;
              }

              this.props.notifications.add({
                id: 'login-message',
                type: 'error',
                title: this.context.i18n.__error(error),
              });

              const {i18n} = this.context;

              if (!i18n.isKnownError(error)) {
                tracker.track('Unknown error occurred', {
                  category: trackingCategory,
                  error,
                });

                // This is unexpected so record it in Sentry
                if (isValidException(error)) {
                  Sentry.captureException(error);
                } else {
                  Sentry.captureMessage('Login Form, session error');
                }
              }
            }
          } else {
            this.props.notifications.dismiss('login-message');
          }
        },
        {
          name: 'Display Login Error Message',
          fireImmediately: true,
        },
      ),
    ];
  }

  componentWillUnmount() {
    this.props.session.resetLogin();
    while (this.listeners.length) { this.listeners.pop()(); }
  }

  @autobind onReceiveFacebookLogin() {
    const {onLoginSuccess, signupCampaign_id} = this.props;

    if (this.voucherValue && this.props.onLoginSuccess) {
      this.props.onLoginSuccess(this.voucherValue);
    }

    if (signupCampaign_id && onLoginSuccess) {
      onLoginSuccess(undefined, undefined, signupCampaign_id);
    }
  }

  @autobind
  @action('onBlurVoucher - LoginForm component')
  onBlurVoucher() {
    if (this.voucherInput.input.value.replace(/\s/g, '').length) {
      this.voucherValue = this.voucherInput.input.value;
    }
  }

  @autobind onReceiveForm(state) {
    const {
      email, password, signupCampaign_id, voucher,
    } = state.body;

    // email, password, oneTimeLogin
    this.props.session.login({
      email, password, undefined, signupCampaign_id,
    })
      .then((response) => {
        this.props.tracker.track('Login Success', {
          category: this.props.trackingCategory,
          label: response.user.id,
        });
        if (this.props.onLoginSuccess) {
          // Gifts page still expects voucher in response
          this.props.onLoginSuccess(voucher);
        }
      }, (error) => {
        this.props.tracker.track('Login Error', {
          category: this.props.trackingCategory,
          label: error,
        });
      });
  }

  @autobind
  @action('onVerifySuccess - LoginForm')
  onVerifySuccess() {
    if (this.props.onLoginSuccess) {
      this.props.onLoginSuccess(this.voucherValue);
    }
  }

  @autobind onValidationFail() {
    this.props.tracker.track('Login Form Validation Error', {
      category: this.props.trackingCategory,
    });
  }

  @autobind voucherInputRef(input) {
    this.voucherInput = input;
  }

  @autobind
  checkValidity() {
    this.passwordInput.checkValidity();
    this.emailInput.checkValidity();
  }

  render() {
    const {i18n} = this.context;
    const {
      location,
      session: {
        isLoading,
        isLoggedIn,
        isVerificationChallengeRequired,
      },
      showVoucherField,
      signupCampaign_id,
      trackingCategory,
    } = this.props;

    const {state} = location;
    let email;
    let password;

    if (state && state.body) {
      ({email, password} = this.props.location.state.body);
    }

    if (isVerificationChallengeRequired) {
      return (
        <VerifyAccountRequest onVerifySuccess={this.onVerifySuccess} trackingCategory={trackingCategory} />
      );
    }

    return (
      <FormHandler method="POST" onReceiveForm={this.onReceiveForm}>
        <Form
          to={location}
          method="POST"
          disabled={isLoading || isLoggedIn}
          className="form"
          onValidationFail={this.onValidationFail}
          checkValidity={this.checkValidity}
        >
          <div className="content--indented">
            <LoginWithFacebook
              onLoginSuccess={this.onReceiveFacebookLogin}
              trackingCategory={trackingCategory}
              signupCampaign_id={signupCampaign_id}
            />

            <Divider className="js-only">{i18n._x('OR', 'form sections separator')}</Divider>

            <InputField
              label={i18n._x('Email Address', 'Login Form')}
              id="email"
              disabled={isLoading || isLoggedIn}
              type="email"
              name="email"
              className="form-control"
              defaultValue={email}
              placeholder={i18n._x('john@smith.com', 'Login Form')}
              required
              newLayout
              darkLayout
              ref={(input) => { this.emailInput = input; }}
            />

            <PasswordField
              label={i18n._x('Password', 'Login Form')}
              id="password"
              disabled={isLoading || isLoggedIn}
              name="password"
              className="form-control"
              defaultValue={password}
              required
              newLayout
              darkLayout
              inputRef={(input) => { this.passwordInput = input; }}
            />

            {signupCampaign_id && (
              <InputField
                id="signupCampaign_id"
                disabled={isLoading || isLoggedIn}
                type="hidden"
                name="signupCampaign_id"
                className="form-control"
                value={signupCampaign_id}
              />
            )}

            {showVoucherField && (
              <InputField
                label={i18n._x('Voucher Code', 'Login Form')}
                id="voucher"
                disabled={isLoading || isLoggedIn}
                type="text"
                name="voucher"
                className="form-control"
                required
                newLayout
                darkLayout
                defaultValue={this.voucherCodeValue}
                ref={this.voucherInputRef}
                onBlur={this.onBlurVoucher}
              />
            )}

            <Button
              data-qa="login-submit"
              disabled={isLoggedIn}
              loading={isLoading}
              block
            >
              {i18n._x('Login', 'Login Form')}
            </Button>
          </div>
        </Form>
      </FormHandler>
    );
  }
}

export default LoginForm;



// WEBPACK FOOTER //
// ./spa/Components/Session/LoginForm.jsx